import { useState } from "react";
import CustomList from '../../../core/components/core/Util/List/CustomList/CustomList';
import CustomListContainer from '../../../core/components/core/Util/List/CustomList/CustomListContainer';
import { loadData } from '../../../core/components/core/Util/List/CustomList/loadData';
import config from '../../config/config';
import { chooseProfileFilter } from '../../helpers/chooseProfileFilter';
import { revenueStatusCell } from '../../helpers/revenueStatusCell';
import { createRevenueActionsCell } from '../../helpers/revenueActionsCell';
import { useDispatch } from 'react-redux';
import ApproveRevenue from './Modals/ApproveRevenue';
import ReproveRevenue from './Modals/ReproveRevenue';
import Fetch from '../../../core/util/core/Fetch';

function List() {
    const profileFilter = chooseProfileFilter();
    const dispatch = useDispatch();

    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showReproveModal, setShowReproveModal] = useState(false);
    const [selectedRevenue, setSelectedRevenue] = useState(null);

    const getInitialOrganizationType = () => {
        return profileFilter.organization_type || 'default';
    };

    const [organizationType, setOrganizationType] = useState(getInitialOrganizationType);
    const [filterValues, setFilterValues] = useState({});

    const openApproveModal = (revenue) => {
        setSelectedRevenue(revenue);
        setShowApproveModal(true);
    };

    const openReproveModal = (revenue) => {
        setSelectedRevenue(revenue);
        setShowReproveModal(true);
    };

    const handleCloseModal = () => {
        setSelectedRevenue(null);
        setShowApproveModal(false);
        setShowReproveModal(false);
        dispatch({ type: 'UPDATE_SESSION', key: 'custom_list_reload', value: new Date() });
    };

    const pathFrontEnd = '/gci/revenues/';
    const endpointBackend = config.endpoints.backend;

    const paths = {
        pathAdd: pathFrontEnd + 'add',
        pathEdit: pathFrontEnd + 'edit',
        pathView: pathFrontEnd + 'view',
        endpointDelete: `${endpointBackend}gci/revenues/delete`,
        endpoint: `${endpointBackend}gci/revenues/getall`,
        endpointGetAllCompanies: `${endpointBackend}gci/companies/getall`,
        endpointGetAllVentures: `${endpointBackend}gci/ventures/getall`,
        endpointGetAllProjects: `${endpointBackend}gci/projects/getall`
    }

    const isDefaultOrganizationType = organizationType === 'default';

    const filters = {
        validateOnChange: true,
        showGoBackButton: false,
        showSubmitButton: false,
        handleSubmit: loadData,
        fields: [
            !profileFilter.gci_company_id ? {
                name: 'gci_company_id',
                type: 'select',
                label: 'Empresa',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllCompanies,
                    label: 'name',
                    value: 'id'
                },
                validations: {
                    required: false
                },
                handleChange: async (element, field, values) => {
                    const companyId = values?.gci_company_id;
                    setFilterValues(values);
                    if (!companyId) {
                        setOrganizationType('default');
                        return;
                    }
                    const company = await Fetch.post(`${config.endpoints.backend}gci/companies/getById`, { id: companyId });
                    setOrganizationType(company?.organization_type || 'default');
                },
                columnSize: 4
            } : {},
            isDefaultOrganizationType ? {
                name: 'gci_venture_id',
                type: 'select',
                label: 'Empreendimento',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllVentures,
                    label: 'enterprise_name',
                    value: 'id',
                    params: {
                        filter: {
                            ...profileFilter
                        }
                    }
                },
                validations: {
                    required: false
                },
                columnSize: 4
            } : {
                name: 'gci_project_id',
                type: 'select',
                label: 'Projeto',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllProjects,
                    label: 'project_name',
                    value: 'id',
                    params: {
                        filter: {
                            ...profileFilter
                        }
                    }
                },
                validations: {
                    required: false
                },
                columnSize: 4
            },
            {
                name: 'status',
                type: 'select',
                label: 'Status',
                options: [
                    { value: 'received', label: 'Recebida' },
                    { value: 'pending', label: 'Pendente' },
                    { value: 'overdue', label: 'Atrasada' },
                    { value: 'partially_received', label: 'Parcialmente Recebida' },
                    { value: 'cancelled', label: 'Cancelada' },
                ],
                validations: {
                    required: false
                },
                columnSize: 4
            },
            {
                name: 'emission_date',
                name_start: 'emission_date_start',
                name_end: 'emission_date_end',
                type: 'date_period',
                label: 'Data de Emissão',
                validations: {
                    required: false,
                },
                columnSize: 4,
            },
            {
                name: 'filtro',
                type: 'submit',
                label: 'Pesquisar',
                columnSize: 1
            },
            {
                name: 'reset',
                type: 'reset',
                label: 'Limpar',
                columnSize: 1
            },
        ]
    }
    filters.values = filterValues;

    let list = {
        labelTitle: 'Lista de Receitas',
        labelTitleButtonAdd: 'Incluir nova receita',
        fieldsShowOnDelete: ['value'],
        canDelete: false,
        formFilters: filters,
        filter: {
            ...profileFilter
        },
        columns: [
            {
                name: 'venture_or_project',
                label: 'Empreendimento/Projeto',
                width: '20%'
            },
            {
                name: 'revenue_type',
                label: 'Tipo'
            },
            {
                name: 'accounting_plan',
                label: 'Plano de Contas',
                width: '20%'
            },
            {
                name: 'installment_number',
                label: 'Parcela',
                width: '8%'
            },
            {
                name: 'value',
                label: 'Valor',
            },
            {
                name: 'total_received',
                label: 'Valor Recebido',
            },
            {
                name: 'payment_method',
                label: 'Forma de Pagamento',
            },
            {
                name: 'due_date',
                label: 'Data de Vencimento',
            },
            {
                name: 'revenue_document_type',
                label: 'Tipo do Documento',
            },
            {
                name: 'document_number',
                label: 'Documento',
            },
            {
                name: 'formattedStatus',
                label: 'Status',
                width: '14%',
                cell: revenueStatusCell
            },
            {
                name: 'approval',
                label: 'Ações',
                width: '10%',
                cell: createRevenueActionsCell(openApproveModal, openReproveModal)
            }
        ],
        columnsButtons: []
    };

    list = { ...list, ...paths };

    return (
        <div>
            <CustomListContainer
                pathAdd={list.pathAdd}
                labelTitle={list.labelTitle}
                labelTitleButtonAdd={list.labelTitleButtonAdd}
                content={<CustomList key={organizationType} list={{ ...list, ...paths }} />}
            />

            {/* --------------------------
                MODAL DE APROVAÇÃO / PAGAMENTO
            --------------------------- */}
            {showApproveModal && (
                <ApproveRevenue
                    show={showApproveModal}
                    onClose={handleCloseModal}
                    revenueId={selectedRevenue?.id}
                />
            )}

            {/* --------------------------
                MODAL DE CANCELAMENTO
            --------------------------- */}
            {showReproveModal && (
                <ReproveRevenue
                    show={showReproveModal}
                    onClose={handleCloseModal}
                    revenueId={selectedRevenue?.id}
                />
            )}
        </div>
    );
}

export default List;
